<script lang="ts" setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import last from 'lodash/last';
import { Drag } from '@/components/dragdrop';
import { DetailsBuilderContract } from '@/components/builder/details';
import { Blueprint } from '@/components/builder/base/blueprints/Blueprint';
import { HasWidth } from '@/components/builder/base/traits/HasWidth';
import { RowBlueprint } from '@/components/builder/form/blueprints/RowBlueprint';
import { Events } from '@/components/builder/base/managers/EventManager';

const instance = getCurrentInstance();

defineOptions({
    name: 'dynamic-details-component-wrapper',
    components: {
        'drag': Drag
    }
});

const props = defineProps({
  "blueprint": null,
  "details": null,
  "parent": null,
  "disabled": { type: Boolean, default: null },
  "disabledToolbar": { type: Boolean,  },
  "disabledDrag": { type: Boolean,  }
});

const primary = ref(true);
const secondary = ref(false);
const insert = ref(false);
const hover = ref(false);
const refreshKey = ref(1);

const design = computed(() => props.details.designMode());
const visible = computed(() => design.value || props.details.expressions.visible(props.blueprint));
const space = computed(() => props.details.dimensions.space(props.parent));
const selected = computed(() => refreshKey.value > 0 && props.details.clipboard.isSelected(props.blueprint) && design.value);

const cut = computed(() => props.details.clipboard.isCut(props.blueprint));
const copy = computed(() => props.details.clipboard.isCopy(props.blueprint));

const smaller = computed(() => props.details.dimensions.smaller(props.blueprint));
const larger = computed(() => props.details.dimensions.larger(props.blueprint));

const available = computed(() => props.details.dimensions.available(props.parent));

const multiplier = computed(() => Math.ceil(12 / props.details.dimensions.rowSpace(props.details.schema.parent(props.blueprint))));
const right = computed(() => last(props.parent.components) == props.blueprint && space.value == 0);

onMounted(() =>
{
    props.details.events.subscribe(Events.FOCUS, onFocus);
});

onUnmounted(() =>
{
    props.details.events.release(Events.FOCUS, onFocus);
});

function onFocus()
{
    if (props.details.clipboard.isSelected(props.blueprint))
    {
        refreshKey.value++;
    }
}

const showPrimaryToolbar = (): void =>
{
    primary.value = true;
    secondary.value = false;
    insert.value = false;
};

const selectComponent = (): void =>
{
    props.details.clipboard.selectComponent(props.blueprint);
    props.details.dom.offsetTop(instance);
    showPrimaryToolbar();
};

const removeComponent = (): void =>
{
    props.details.layout.removeComponent(props.blueprint);
};

const showSecondaryToolbar = (): void =>
{
    primary.value = false;
    secondary.value = true;
    insert.value = false;
};

const showInsertToolbar = (): void =>
{
    primary.value = false;
    secondary.value = false;
    insert.value = true;
};
</script>

<template>
    <dynamic-details-component class="component" :class="['col-12', `col-md-${blueprint.width * multiplier}`]" :details="details" :blueprint="blueprint" v-if="visible">
        <dynamic-details-design-status :details="details" :blueprint="blueprint" />
        <drag
            :draggable="design && !disabledDrag" :transfer-data="{ component: blueprint }" :class="{'wrapper': design,'selected': design && selected, 'hover': design && hover, 'wrapper--minus-margin': !design}"
            @mouseover.stop="hover = true" @mouseout.stop="hover = false" @dragstart="selectComponent()" @click.stop="selectComponent()"
        >
            <div :class="{'disabled': disabled === null ? design : disabled, 'fade': cut}">
                <slot name="default"></slot>
            </div>
        </drag>
        <portal to="dynamic-details-toolbox" v-if="design && selected && $slots.properties">
            <dynamic-details-design-panel :details="details" :key="blueprint.name">
                <slot name="properties"></slot>
            </dynamic-details-design-panel>
        </portal>
        <dynamic-details-design-toolbar v-if="design && selected && !disabledToolbar" :right="right" @click.stop.prevent>
            <div class="btn-toolbar flex-nowrap" v-if="primary">
                <div class="btn-group">
                    <button type="button" class="btn btn-success" :title="$t('[[[Wstaw kontrolkę]]]')" @click.stop.prevent="showInsertToolbar()">
                        <i class="far fa-plus"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Kopiuj]]]')" :disabled="copy" @click.stop.prevent="details.clipboard.copy(blueprint)">
                        <i class="far fa-copy"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Wytnij]]]')" :disabled="cut" @click.stop.prevent="details.clipboard.cut(blueprint)">
                        <i class="far fa-cut"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-danger" :title="$t('[[[Usuń]]]')" @click.stop.prevent="removeComponent()">
                        <i class="far fa-trash"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-light" :title="$t('[[[Więcej...]]]')" @click.stop.prevent="showSecondaryToolbar()">
                        <i class="far fa-ellipsis-h"></i>
                    </button>
                </div>
            </div>
            <div class="btn-toolbar flex-nowrap" v-if="secondary">
                <div class="btn-group">
                    <button type="button" class="btn btn-light" :title="$t('[[[Anuluj]]]')" @click.stop.prevent="showPrimaryToolbar()">
                        <i class="far fa-ban"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Przesuń do góry]]]')" @click.stop.prevent="details.layout.moveUp(parent)">
                        <i class="far fa-arrow-up"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Przesuń na dół]]]')" @click.stop.prevent="details.layout.moveDown(parent)">
                        <i class="far fa-arrow-down"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Zmniejsz]]]')" :disabled="!smaller" @click.stop.prevent="details.dimensions.shrink(blueprint)">
                        <i class="far fa-arrow-left-from-line"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Powiększ]]]')" :disabled="!larger" @click.stop.prevent="details.dimensions.enlarge(blueprint)">
                        <i class="far fa-arrow-right-from-line"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Ustaw szerokości proporcjonalnie]]]')" @click.stop.prevent="details.dimensions.autoarrange(parent)">
                        <i class="far fa-arrows-left-right-to-line"></i>
                    </button>
                </div>
            </div>
            <div class="btn-toolbar flex-nowrap" v-if="insert">
                <div class="btn-group">
                    <button type="button" class="btn btn-light" :title="$t('[[[Anuluj]]]')" @click.stop.prevent="showPrimaryToolbar()">
                        <i class="far fa-ban"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Wstaw z lewej strony]]]')" :disabled="!available" @click.stop.prevent="details.layout.addBefore(blueprint)">
                        <i class="far fa-arrow-to-left"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Wstaw z prawej strony]]]')" :disabled="!available" @click.stop.prevent="details.layout.addAfter(blueprint)">
                        <i class="far fa-arrow-to-right"></i>
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Wstaw z góry]]]')" @click.stop.prevent="details.layout.addRowAfter(parent)">
                        <i class="far fa-arrow-to-top"></i>
                    </button>
                    <button type="button" class="btn btn-secondary" :title="$t('[[[Wstaw z dołu]]]')" @click.stop.prevent="details.layout.addRowAfter(parent)">
                        <i class="far fa-arrow-to-bottom"></i>
                    </button>
                </div>
            </div>
        </dynamic-details-design-toolbar>
    </dynamic-details-component>
</template>

<style lang="scss" scoped>
.component {
    position: relative;
    transition: width 0.5s ease-in-out;

    .wrapper {
        height: 100%;
        padding: 8px;
        border: 1px solid transparent;
        background: transparent;
        transition: width 0.25s ease;

        .disabled {
            pointer-events: none;
            height: 100%;
        }

        &--minus-margin {
            padding: 4px;
            margin: 0 -4px;
            height: 100%;

            > div {
                height: 100%;
            }
        }

        .fade {
            opacity: 0.5;
        }

        &.hover {
            background: rgb(var(--bs-warning) 0.02);
            border: 1px solid var(--bs-warning);
        }

        &.selected {
            background: rgb(var(--bs-warning) 0.02);
            border: 1px solid var(--bs-warning);
        }
    }
}
</style>

